import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDebounceFn } from 'ahooks';
import { ISearchPdfToolBar, ICurrentInfo, IUseSearchPdfToolBarReturn, IStaticInfo } from '../interface';
import { defaultCurrentInfo } from '../dataFlow/index';
import { KEYBOARD } from '../components/constants';
const useSearchPdfToolBar = (data: ISearchPdfToolBar): IUseSearchPdfToolBarReturn => {
    const { onSearch, onClose, onJumpToElement, currentPage } = data;
    const [currentInfo, setCurrentInfo] = useState<ICurrentInfo>({ ...defaultCurrentInfo });
    const searchingValueRef = useRef<string>('');
    const latestExecuteIdRef = useRef(0);
    const isComposingRef = useRef(false);

    const updateCurrentInfo = (info: Partial<ICurrentInfo>) => {
        setCurrentInfo((prev) => ({ ...prev, ...info }));
    };

    const resetSearchResult = () => {
        updateCurrentInfo({ list: [], total: 0, currentIndex: 0, isLoading: false });
    };

    const reset = () => {
        searchingValueRef.current = '';
        updateCurrentInfo({ ...defaultCurrentInfo });
    };

    const onClosePanel = (isReset: boolean = false) => {
        if (isReset) {
            // 清空高亮（吞掉错误，避免未处理 Promise）
            searchingValueRef.current !== '' && void onSearch('').catch(() => { });
            reset();
        }
        onClose();
    };

    const close = (isReset: boolean = false) => {
        onClosePanel(isReset);
    };

    type SearchTrigger = 'debounce' | 'enter';

    // 执行搜索的核心逻辑
    const executeSearch = async (value: string, trigger: SearchTrigger) => {
        const executeId = ++latestExecuteIdRef.current;
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            resetSearchResult();
            searchingValueRef.current = '';
            // 清空高亮（吞掉错误，避免未处理 Promise）
            void onSearch('').catch(() => { });
            return;
        }
        searchingValueRef.current = trimmedValue;
        updateCurrentInfo({ isLoading: true });

        try {
            const searchResults = await onSearch(trimmedValue);
            // 仅处理最后一次触发的搜索（避免并发 promise 覆盖）
            if (executeId !== latestExecuteIdRef.current) {
                return;
            }
            // 检查搜索值是否已改变，避免使用过期的搜索结果
            if (searchingValueRef.current !== trimmedValue) {
                return;
            }

            const list = searchResults || [];
            const total = list.length;
            if (total === 0) {
                // 无结果：清空列表与 loading，不做跳转/选中
                updateCurrentInfo({
                    list: [],
                    total: 0,
                    currentIndex: 0,
                    isLoading: false,
                });
                return;
            }

            // results 已保证：按页从小到大、同页按 index 从小到大
            let targetIndex = 0;
            const pageNum = typeof currentPage === 'number' && currentPage > 0 ? currentPage : undefined;
            let hasMatchOnCurrentPage = false;
            if (pageNum) {
                const idx = list.findIndex((it) => it.page === pageNum);
                if (idx >= 0) {
                    targetIndex = idx;
                    hasMatchOnCurrentPage = true;
                }
            }

            updateCurrentInfo({
                list,
                total,
                currentIndex: targetIndex,
                isLoading: false,
            });

            const target = list[targetIndex];
            const shouldAutoJump =
                hasMatchOnCurrentPage || (pageNum !== undefined && !hasMatchOnCurrentPage) || trigger === 'enter';

            if (shouldAutoJump) {
                void onJumpToElement(target);
            }
        } catch {
            if (executeId !== latestExecuteIdRef.current) {
                return;
            }
            // 检查搜索值是否已改变，避免在已改变的值上重置状态
            if (searchingValueRef.current !== trimmedValue) {
                return;
            }
            resetSearchResult();
        }
    };

    const { run: debouncedSearch, cancel: cancelSearch } = useDebounceFn((v: string) => executeSearch(v, 'debounce'), {
        wait: 300,
    });

    // 组件卸载时清理防抖函数
    useEffect(() => {
        return () => {
            cancelSearch();
        };
    }, [cancelSearch]);

    const looksLikeImeInterimValue = useCallback((value: string) => {
        // 兜底：部分 WebView/浏览器下 isComposing 可能不可靠
        // 特征：包含中文/日文/韩文等 CJK 字符 + 末尾是一段纯字母（如 “我的shi”）
        // 注意：这会牺牲一部分“中文+英文后缀”的即时搜索，但能避免组合输入中间态误触发导致结果闪烁/丢失。
        const hasCjk = /[\u3040-\u30ff\u3400-\u9fff\uf900-\ufaff\uac00-\ud7af]/.test(value);
        if (!hasCjk) {
            return false;
        }
        const tail = value.match(/[A-Za-z]+$/)?.[0];
        if (!tail) {
            return false;
        }
        // 更像拼音：短、小写字母串（如 shi / shijie / wo）
        if (tail.length <= 8 && /^[a-z]+$/.test(tail)) {
            return true;
        }
        return false;
    }, []);

    const onBeforeInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
        // 在一些环境里 beforeinput 对组合输入更敏感：
        // - inputType: 'insertCompositionText' / 'deleteCompositionText'
        const ne = e.nativeEvent as unknown as { inputType?: string; isComposing?: boolean };
        if (ne?.isComposing) {
            isComposingRef.current = true;
            return;
        }
        if (typeof ne?.inputType === 'string' && ne.inputType.toLowerCase().includes('composition')) {
            isComposingRef.current = true;
        }
    }, []);

    const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        updateCurrentInfo({ value });
        // 组合输入（如中文拼音）过程中会频繁触发 onChange：
        // - 仅更新输入框展示，不触发搜索/取消，避免“搜索导致输入被打断/结果中断”
        // - 在 compositionend 再统一触发一次搜索
        if (
            isComposingRef.current ||
            (e.nativeEvent as unknown as { isComposing?: boolean })?.isComposing ||
            looksLikeImeInterimValue(value)
        ) {
            return;
        }

        cancelSearch();

        // 输入为空时：同步清空结果与高亮
        if (!value.trim()) {
            searchingValueRef.current = '';
            resetSearchResult();
            void onSearch('').catch(() => { });
            return;
        }
        debouncedSearch(value);
    };

    // 立即执行搜索（用于回车键触发）
    const searchImmediately = (value: string) => {
        cancelSearch();
        updateCurrentInfo({ value });
        void executeSearch(value, 'enter');
    };

    const onCompositionStart = useCallback((_: React.CompositionEvent<HTMLInputElement>) => {
        isComposingRef.current = true;
    }, []);

    const onCompositionEnd = useCallback(
        (e: React.CompositionEvent<HTMLInputElement>) => {
            isComposingRef.current = false;
            // 不同浏览器/React 版本中 compositionend 与 onChange 的触发顺序可能不同，
            // 这里直接用最终值触发一次搜索，确保组合输入结束后会更新结果。
            const value = e.currentTarget.value;
            updateCurrentInfo({ value });

            cancelSearch();
            if (!value.trim()) {
                searchingValueRef.current = '';
                resetSearchResult();
                void onSearch('').catch(() => { });
                return;
            }
            debouncedSearch(value);
        },
        [cancelSearch, debouncedSearch, onSearch],
    );

    const getItemByIndex = (index: number): [boolean, IStaticInfo | undefined, number] => {
        const length = currentInfo.list.length;
        if (length === 0) {
            return [false, undefined, 0];
        }
        const tmpIndex = index % length;
        let actualIndex: number;
        if (tmpIndex < 0) {
            actualIndex = length + tmpIndex;
        } else {
            actualIndex = tmpIndex;
        }
        return [true, currentInfo.list[actualIndex], actualIndex];
    };
    const onSelectElementByIndex = async (index: number) => {
        const [success, itemInfo, actualIndex] = getItemByIndex(index);
        if (!success || !itemInfo) {
            return false;
        }
        // 更新当前索引
        updateCurrentInfo({ currentIndex: actualIndex });
        await onJumpToElement(itemInfo);
        return true;
    };
    const onNext = async () => {
        return onSelectElementByIndex(currentInfo.currentIndex + 1);
    };
    const onPrevious = async () => {
        return onSelectElementByIndex(currentInfo.currentIndex - 1);
    };
    const setSelectedElement = (element: IStaticInfo) => {
        const tmpList = [...currentInfo.list];
        // 通过 rows, col, page 三个字段组合来匹配元素
        const index = tmpList.findIndex((item) => {
            if (item.index !== undefined && element.index !== undefined) {
                return item.index === element.index && item.page === element.page;
            }
            return item.rows === element.rows && item.col === element.col && item.page === element.page;
        });
        if (index !== -1) {
            return onSelectElementByIndex(index);
        }
        return Promise.resolve(false);
    };

    // 是否有搜索结果
    const hasResults = useMemo(() => currentInfo.total > 0, [currentInfo.total]);

    const clearInputAndHighlights = useCallback(() => {
        cancelSearch();
        searchingValueRef.current = '';
        resetSearchResult();
        updateCurrentInfo({ value: '' });
        void onSearch('').catch(() => { });
    }, [cancelSearch, onSearch]);

    // 键盘事件处理
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            // 避免打断中文/日文输入法组合键
            if (isComposingRef.current || (e.nativeEvent as unknown as { isComposing?: boolean })?.isComposing) {
                return;
            }
            const key = e.key;
            const stopInputDefault = () => {
                e.preventDefault();
                e.stopPropagation();
            };

            switch (key) {
                case KEYBOARD.ENTER: {
                    // 回车：Shift+Enter 上一个；Enter 下一个/立即搜索
                    stopInputDefault();
                    const trimmed = currentInfo.value.trim();
                    if (!trimmed) {
                        return;
                    }
                    if (e.shiftKey) {
                        if (hasResults) {
                            void onPrevious();
                        }
                        return;
                    }
                    if (searchingValueRef.current === trimmed) {
                        if (hasResults) {
                            void onNext();
                        } else {
                            // 同词但没有结果：允许重新搜索一次（比如 PDF 切页/渲染完成后）
                            searchImmediately(trimmed);
                        }
                        return;
                    }
                    searchImmediately(trimmed);
                    return;
                }
                case KEYBOARD.ARROW_UP: {
                    // ↑：上一条结果（并阻止光标移动）
                    stopInputDefault();
                    if (hasResults) {
                        void onPrevious();
                    }
                    return;
                }
                case KEYBOARD.ARROW_DOWN: {
                    // ↓：下一条结果（并阻止光标移动）
                    stopInputDefault();
                    if (hasResults) {
                        void onNext();
                    }
                    return;
                }
                case KEYBOARD.ESC: {
                    // Esc：有输入则清空；否则关闭面板（与关闭按钮一致：reset + 清高亮）
                    stopInputDefault();
                    if (currentInfo.value.trim() || searchingValueRef.current) {
                        clearInputAndHighlights();
                        return;
                    }
                    onClosePanel(true);
                    return;
                }
                default:
                    return;
            }
        },
        [clearInputAndHighlights, currentInfo.value, hasResults, onClosePanel, onNext, onPrevious, searchImmediately],
    );

    return {
        ...currentInfo,
        reset,
        close,
        setSelectedElement,
        onClosePanel,
        onChangeValue,
        onBeforeInput,
        onCompositionStart,
        onCompositionEnd,
        searchImmediately,
        onNext,
        onPrevious,
        handleKeyDown,
        onFocus: () => { },
        getCurrentInfo: () => currentInfo,
    };
};
export { useSearchPdfToolBar };
